import { getPathProductBySlug } from 'constants/Paths';
import { DOMAIN_WEB_HOST, ENV_COUNTRY } from 'sysconfig';

export const INSIDER_PAGE_TYPE = {
  CONFIRMATION: 'Confirmation',
  OTHER: 'Other',
};

// ─── Currency & Language mapping ──────────────────────────────────────────────

/**
 * Map ENV_COUNTRY → mã tiền tệ ISO 4217 (3 ký tự).
 * Insider SDK BẮT BUỘC ISO code (THB, VND, USD...), KHÔNG nhận symbol (฿, đ, $).
 */
const mapCurrencyISO = {
  th: 'THB',
  vn: 'VND',
  kh: 'USD',
  cam: 'USD',
  en: 'USD',
};
const CURRENCY_ISO = mapCurrencyISO[ENV_COUNTRY] || 'THB';

/**
 * Map mã ngôn ngữ đơn (th, vi, en) → Locale identifier (th_TH, vi_VN, en_US).
 * Insider SDK BẮT BUỘC định dạng locale (vd: en_US, tr_TR, fr_FR).
 */
const mapLocaleIdentifier = {
  th: 'th_TH',
  vi: 'vi_VN',
  en: 'en_US',
  km: 'km_KH',
};
const toLocale = (lang) => mapLocaleIdentifier[lang] || `${lang}_${(lang || '').toUpperCase()}`;

/**
 * Gán DOMAIN_WEB_HOST làm prefix cho URL (image hoặc product path).
 * Nếu URL đã là absolute (bắt đầu bằng http:// hoặc //) thì giữ nguyên.
 * Nếu URL là relative path (bắt đầu bằng /) thì thêm domain prefix.
 */
const toAbsoluteUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return path;
  }
  return `${DOMAIN_WEB_HOST}${path.startsWith('/') ? '' : '/'}${path}`;
};

// ─── Core queue helper ────────────────────────────────────────────────────────

const getQueue = () => {
  if (typeof window === 'undefined') return null;
  window.InsiderQueue = window.InsiderQueue || [];
  return window.InsiderQueue;
};

/**
 * Push { type: 'init' } — PHẢI được gọi DUY NHẤT một lần mỗi page view.
 * Sử dụng flag window.__insiderInitCalled để ngăn duplicate.
 * Flag tự động reset khi gọi resetInsiderInit() (trước mỗi page navigation).
 */
export const insiderInit = () => {
  const queue = getQueue();
  if (!queue) return;
  if (window.__insiderInitCalled) return;
  window.__insiderInitCalled = true;
  queue.push({ type: 'init' });
};

/**
 * Reset flag cho phép gọi insiderInit() lại — gọi trước khi push data cho page mới.
 * Cần dùng khi chuyển trang (SPA navigation) để init có thể chạy lại.
 */
export const resetInsiderInit = () => {
  if (typeof window !== 'undefined') {
    window.__insiderInitCalled = false;
  }
};

// ─── Data converters (pure, không side-effect) ───────────────────────────────

const convertPhoneNumber = (phone) => {
  try {
    if (!phone) return '';
    if (phone.startsWith('+66')) return phone;
    if (phone.startsWith('66')) return `+${phone}`;
    if (phone.startsWith('0')) return `+66${phone.substring(1, phone.length)}`;
    return `+66${phone}`;
  } catch (error) {
    console.error('Insider convertPhoneNumber error:', error);
  }
  return '';
};

/**
 * Mapping user → Insider user attributes.
 * Quy tắc kiểu dữ liệu mặc định theo Insider SDK:
 *   - String  → '' (chuỗi rỗng, KHÔNG dùng null)
 *   - Number  → 0
 *   - Boolean → true / false
 */
const convertUser = (user) => ({
  uuid: user?.customerID?.toString() || user?.code?.toString() || '',
  email: user?.email || '',
  email_optin: Boolean(user?.email),
  phone_number: convertPhoneNumber(user?.phone || user?.phoneNumber),
  sms_optin: true,
  name: user?.name || '',
  username: user?.username || user?.code || '',
  gdpr_optin: true,
  custom: {
    orders_count: Number(user?.ordersCount) || 0,
    is_activated: Boolean(user?.isActive),
    loyalty_point: Number(user?.point) || 0,
  },
});

/**
 * Mapping guest (user ẩn danh) → Insider user attributes.
 * Dùng để thu thập opt-in từ visitor chưa đăng nhập.
 * gdpr_optin: true để Insider có thể tạo profile và collect data.
 */
const convertGuestUser = () => ({
  uuid: '',
  email: '',
  email_optin: false,
  phone_number: '',
  sms_optin: false,
  name: '',
  username: '',
  gdpr_optin: true,
});

/**
 * Mapping cart item → cấu trúc Insider cart item (dùng cho cart sync & cart page).
 * Quy tắc: String → '', Number → 0.
 */
const convertCartItem = ({ item }) =>
  item && {
    id: String(item.productId || item.productID || ''),
    name: item.name || '',
    taxonomy: item.categoryCodes ? item.categoryCodes : [],
    unit_price: Number(item.currentPrice || item.salePrice || 0) || 0,
    unit_sale_price: Number(item.dealPrice || item.deal?.price || item.price || 0) || 0,
    quantity: Number(item.quantity) || 0,
    url: toAbsoluteUrl(getPathProductBySlug(item.slug)) || '',
    product_image_url: toAbsoluteUrl((item.imagesProxy && item.imagesProxy[0]) || ''),
  };

/**
 * Mapping order item → cấu trúc Insider purchase item.
 * Quy tắc: String → '', Number → 0, Array → [].
 */
const convertPurchaseItem = (item) =>
  item && {
    id: String(item.productId || item.productID || ''),
    name: item.name || '',
    taxonomy: [],
    unit_price: Number(item.price || item.salePrice || 0) || 0,
    unit_sale_price: Number(item.deal?.price || item.salePrice || item.price || 0) || 0,
    url: toAbsoluteUrl(getPathProductBySlug(item.slug)) || '',
    product_image_url: toAbsoluteUrl((item.imagesProxy && item.imagesProxy[0]) || ''),
    quantity: Number(item.quantity) || 0,
  };

/**
 * Mapping product → cấu trúc Insider add/remove cart event item.
 * Quy tắc: String → '', Number → 0, Array → [].
 */
const convertCartEventItem = (product) => ({
  id: String(product.productId || product.skuId || ''),
  name: product.name || '',
  taxonomy: product.categoryCodes ? product.categoryCodes : [],
  unit_price: Number(product.price || product.salePrice || 0) || 0,
  unit_sale_price: Number(product.deal?.price || product.salePrice || product.price || 0) || 0,
  currency: CURRENCY_ISO,
  url: toAbsoluteUrl(getPathProductBySlug(product.slug)) || '',
  product_image_url: toAbsoluteUrl(
    (product.imagesProxy && product.imagesProxy[0]) || product.imageUrls?.[0] || '',
  ),
  quantity: Number(product.quantity) || 0,
});

// ─── Push-only helpers (KHÔNG tự gọi init) ───────────────────────────────────

/**
 * Push { type: 'currency', value: 'THB' } — ISO 4217, bắt buộc trước init.
 */
const pushCurrency = (queue) => {
  queue.push({ type: 'currency', value: CURRENCY_ISO });
};

/**
 * Push { type: 'language', value: 'th_TH' } — Locale identifier, bắt buộc trước init.
 */
const pushLanguage = (queue, language) => {
  queue.push({ type: 'language', value: toLocale(language) });
};

/**
 * Push thông tin user (không kèm init).
 */
const pushUser = (queue, user) => {
  if (!user) return;
  queue.push({ type: 'user', value: convertUser(user) });
};

/**
 * Push trạng thái giỏ hàng (không kèm init).
 * SDK yêu cầu: total, subtotal, shipping_cost, quantity, items (Array).
 * @param {object} queue
 * @param {Array}  cartItems  - Danh sách sản phẩm
 * @param {object} cartTotals - { total, subtotal, shippingCost, quantity }
 */
const pushCart = (queue, cartItems, cartTotals = {}) => {
  const items = Array.isArray(cartItems)
    ? cartItems.map((item) => convertCartItem({ item })).filter(Boolean)
    : [];
  const totalQuantity = items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  queue.push({
    type: 'cart',
    value: {
      total: Number(cartTotals.total) || 0,
      subtotal: Number(cartTotals.subtotal) || 0,
      shipping_cost: Number(cartTotals.shippingCost) || 0,
      quantity: Number(cartTotals.quantity) || totalQuantity,
      currency: CURRENCY_ISO,
      items,
    },
  });
};

/**
 * Push page type (không kèm init).
 */
const pushPage = (queue, pageData) => {
  queue.push({ type: 'page', data: pageData });
};

// ─── Các hàm public giữ nguyên API cũ (tự gọi init để backward-compat) ──────

/**
 * Đồng bộ giỏ hàng — gọi từ CartReducer mỗi khi cart state thay đổi.
 * KHÔNG gọi insiderInit() vì hàm này chạy nhiều lần trong vòng đời trang.
 * init sẽ do page orchestrator (initHomePage, trackCartPage, v.v.) đảm nhận.
 */
const updateCartItem = (cartItems) => {
  const queue = getQueue();
  if (!queue || !cartItems) return;
  pushCart(queue, cartItems);
};

/**
 * Push user data vào queue — gọi từ AuthContext (root-level) cho cả logged-in lẫn guest.
 * Sử dụng flag window.__insiderUserPushed để đảm bảo chỉ push 1 lần
 * trong suốt vòng đời ứng dụng (application load).
 * Khi user thay đổi (login/logout), flag sẽ được reset và push lại.
 *
 * - user khả dụng → push user attributes thật.
 * - user null/undefined (guest) → push anonymous user với gdpr_optin: true
 *   để Insider tạo profile ẩn danh và collect opt-in.
 *
 * KHÔNG gọi insiderInit() — init sẽ do page orchestrator đảm nhận.
 */
const initUser = (user) => {
  const queue = getQueue();
  if (!queue) return;

  if (!user) {
    // Guest path — uuid sentinel '__guest__' để phân biệt với logged-in user (uuid='').
    if (window.__insiderUserPushed && window.__insiderUserUuid === '__guest__') return;
    window.__insiderUserPushed = true;
    window.__insiderUserUuid = '__guest__';
    queue.push({ type: 'user', value: convertGuestUser() });
    return;
  }

  // Logged-in path — so sánh uuid hiện tại để không push lại cùng user.
  const newUuid = user?.customerID?.toString() || user?.code?.toString() || '';
  if (window.__insiderUserPushed && window.__insiderUserUuid === newUuid) return;
  window.__insiderUserPushed = true;
  window.__insiderUserUuid = newUuid;
  pushUser(queue, user);
};

/**
 * Reset flag user pushed — gọi khi logout để cho phép push user mới khi login lại.
 */
export const resetInsiderUser = () => {
  if (typeof window !== 'undefined') {
    window.__insiderUserPushed = false;
    window.__insiderUserUuid = '';
  }
};

/**
 * Trang Confirmation — push page rồi gọi init ngay.
 */
const confirmation = () => {
  const queue = getQueue();
  if (!queue) return;
  resetInsiderInit();
  pushPage(queue, { type: 'Confirmation' });
  insiderInit();
};

/**
 * Đăng ký thành công — push user + page rồi gọi init ngay.
 */
const registerSuccess = (user) => {
  const queue = getQueue();
  if (!queue || !user) return;
  resetInsiderInit();
  pushUser(queue, user);
  pushPage(queue, { type: 'dangky_success' });
  insiderInit();
};

// ─── Hàm orchestrator cho từng page (currency → language → page event → init) ───
// User được push độc lập ở root-level (AuthContext.initUser) cho cả logged-in lẫn guest.
// Cart snapshot chỉ push trong trackCartPage / trackAddToCart / trackRemoveFromCart.

/**
 * Khởi tạo Insider tracking cho trang Home.
 * Thứ tự push: currency → language → home → init (1 lần).
 * @param {object} options
 * @param {string} options.language - Mã ngôn ngữ (vd: 'th', 'vi')
 */
export const initHomePage = ({ language = 'th' } = {}) => {
  const queue = getQueue();
  if (!queue) return;
  resetInsiderInit();
  pushCurrency(queue);
  pushLanguage(queue, language);
  queue.push({ type: 'home' });
  insiderInit();
};

/**
 * Orchestrator cho trang xác nhận đơn hàng (Purchase / Confirmation Page).
 * Thứ tự push: currency → language → purchase → init (1 lần).
 * @param {object} options
 * @param {object} options.order    - Dữ liệu đơn hàng
 * @param {string} options.language - Mã ngôn ngữ
 */
export const trackPurchase = ({ order, language = 'th' } = {}) => {
  const queue = getQueue();
  if (!queue || !order) return;
  try {
    resetInsiderInit();
    pushCurrency(queue);
    pushLanguage(queue, language);
    const { orderId, orderCode, totalPrice, products = [], orderItems = [] } = order;
    const sourceItems = products.length ? products : orderItems;
    queue.push({
      type: 'purchase',
      value: {
        order_id: String(orderId || orderCode || ''),
        total: Number(totalPrice) || 0,
        currency: CURRENCY_ISO,
        items: sourceItems.map(convertPurchaseItem).filter(Boolean),
      },
    });
    insiderInit();
  } catch (error) {
    console.error('Insider trackPurchase error:', error);
  }
};

/**
 * Tracking thêm sản phẩm vào giỏ (Add to Cart event).
 * Push add_to_cart + cart snapshot. KHÔNG gọi insiderInit() vì init đã fire ở page orchestrator.
 */
export const trackAddToCart = ({ product, cartItems, cartTotals }) => {
  const queue = getQueue();
  if (!queue || !product) return;
  resetInsiderInit();
  queue.push({ type: 'add_to_cart', value: convertCartEventItem(product) });
  pushCart(queue, cartItems, cartTotals);
};

/**
 * Tracking xóa sản phẩm khỏi giỏ (Remove from Cart event).
 * Push remove_from_cart + cart snapshot. KHÔNG gọi insiderInit() vì init đã fire ở page orchestrator.
 */
export const trackRemoveFromCart = ({ product, cartItems, cartTotals }) => {
  const queue = getQueue();
  if (!queue || !product) return;
  resetInsiderInit();
  queue.push({ type: 'remove_from_cart', value: convertCartEventItem(product) });
  pushCart(queue, cartItems, cartTotals);
};

/**
 * Orchestrator cho trang giỏ hàng (Cart Page View).
 * Thứ tự push: currency → language → cart → init (1 lần).
 * @param {object} options
 * @param {Array}  options.cartItems  - Danh sách sản phẩm trong giỏ hàng
 * @param {object} options.cartTotals - { total, subtotal, shippingCost, quantity }
 * @param {string} options.language   - Mã ngôn ngữ
 */
export const trackCartPage = ({ cartItems = [], cartTotals = {}, language = 'th' } = {}) => {
  const queue = getQueue();
  if (!queue) return;
  resetInsiderInit();
  pushCurrency(queue);
  pushLanguage(queue, language);
  pushCart(queue, cartItems, cartTotals);
  insiderInit();
};

/**
 * Orchestrator cho trang chi tiết sản phẩm (Product Detail Page).
 * Thứ tự push: currency → language → product → init (1 lần).
 * @param {object} options
 * @param {object} options.product  - Dữ liệu sản phẩm từ API
 * @param {string} options.language - Mã ngôn ngữ (vd: 'th', 'vi')
 */
export const trackProductDetail = ({ product, language = 'th' } = {}) => {
  const queue = getQueue();
  if (!queue || !product) return;
  resetInsiderInit();
  pushCurrency(queue);
  pushLanguage(queue, language);
  queue.push({
    type: 'product',
    value: {
      id: String(product.productId || product.skuId || ''),
      name: product.name || '',
      taxonomy: product.categoryCodes ? product.categoryCodes : [],
      unit_price: Number(product.price || product.salePrice || 0) || 0,
      unit_sale_price: Number(product.deal?.price || product.salePrice || product.price || 0) || 0,
      currency: CURRENCY_ISO,
      url: toAbsoluteUrl(getPathProductBySlug(product.slug)) || '',
      product_image_url: toAbsoluteUrl(product.imageUrls?.[0] || product.defaultImage || ''),
      custom: {
        seller_code: product.sellerCode,
      },
    },
  });
  insiderInit();
};

/**
 * Orchestrator cho trang danh sách sản phẩm (Listing / Category Page).
 * Thứ tự push: currency → language → category → init (1 lần).
 * @param {object}   options
 * @param {string[]} options.taxonomy     - Mảng tên danh mục
 * @param {string}   options.language     - Mã ngôn ngữ
 * @param {Array}    options.productsInfo - Danh sách sản phẩm trong listing
 */
export const trackListingPage = ({ taxonomy = [], language = 'th', productsInfo = [] } = {}) => {
  const queue = getQueue();
  if (!queue) return;
  resetInsiderInit();
  pushCurrency(queue);
  pushLanguage(queue, language);
  queue.push({
    type: 'category',
    value: {
      taxonomy: Array.isArray(taxonomy) ? taxonomy : [],
      breadcrumb: Array.isArray(taxonomy) ? taxonomy : [],
      products: productsInfo,
    },
  });
  insiderInit();
};

/**
 * Cập nhật loại trang (page type) — dùng cho các trang không có orchestrator riêng.
 * Vẫn tự gọi init để backward-compatible với các trang hiện tại.
 */
export function setPageType(type) {
  const queue = getQueue();
  if (!queue) return;
  resetInsiderInit();
  queue.push({ type: 'page', data: { type } });
  insiderInit();
}

export default {
  initUser,
  confirmation,
  convertCartItem,
  updateCartItem,
  registerSuccess,
  setPageType,
  trackPurchase,
  initHomePage,
};
